import React, { useEffect, useState } from 'react';
import { Message } from '@/type/chat';
import { useTranslation } from '@/lib/i18n';
import { useRightDrawer } from '@/contexts/DrawerContext';
import MarkdownEditor from '../DeepSearch/MarkdownEditor';
import { Pen, History } from 'lucide-react';
import { researchFetch } from '@/lib/utils/fetch';
import { styled } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import { useTheme } from 'next-themes';
import MenuItem from '@mui/material/MenuItem';
import dayjs from 'dayjs';
import { useSnackbar } from '@/contexts/SnackbarContext';

interface Version {
  versionNumber: number;
  content: string;
  title: string;
  createdAt: string;
  [k: string]: any;
}

export type EditIProps = {
  message: Message;
  editReport?: (message: Message) => void;
};

export default function Edit(props: EditIProps) {
  const { message, editReport } = props;
  const { t } = useTranslation();

  const { openDrawer, setDrawerWidth } = useRightDrawer();

  const handleVersion = (content: string, version?: number) => {
    openDrawer(
      <MarkdownEditor
        message={message}
        mdContent={content}
        editReport={handleEditReport}
        version={version}
        editable={false}
      />,
      <EditotHeader message={message} onVersionChange={handleVersion} />,
    );
  };

  const handleEditReport = (content: string) => {
    editReport?.({ ...message, content });
  };

  const handleEdit = () => {
    setDrawerWidth(Math.min(document.body.clientWidth, 600));
    openDrawer(
      <MarkdownEditor message={message} editReport={handleEditReport} />,
      <EditotHeader message={message} onVersionChange={handleVersion} />,
    );
  };

  return (
    <button
      className="flex flex-row items-center  h-[32px] px-[10px] gap-1.5 text-black/70 dark:text-text-tertiary text-sm rounded-[16px] hover:bg-[#E9E9E9] dark:hover:bg-[#1e1e1e] transition duration-200 hover:text-black dark:hover:text-white"
      onClick={handleEdit}
    >
      <Pen size={14} />
      {t('Edit')}
    </button>
  );
}

const StyledPopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    backgroundColor: 'var(--popover-bg, #ffffff)',
    borderRadius: '12px',
    marginTop: '10px',
    minWidth: 270,
    color: 'var(--popover-text, #000000)',
    boxShadow: '0px 10px 38px -10px rgba(0, 0, 0, 0.5), 0px 10px 20px -15px rgba(0, 0, 0, 0.2)',
    border: '1px solid var(--popover-border, #e5e5e5)',
    '& .MuiList-root': {
      padding: '8px',
    },
    '& .MuiMenuItem-root': {
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: 'var(--popover-text, #000000)',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: 'var(--popover-hover, #f5f5f5)',
      },
      '& .MuiSvgIcon-root': {
        fontSize: 20,
        color: 'var(--popover-text, #000000)',
      },
    },
    '& .MuiDivider-root': {
      margin: '8px 0',
      backgroundColor: 'var(--popover-divider, #e5e5e5)',
    },
  },
}));

function EditotHeader({
  message,
  onVersionChange,
}: {
  message: Message;
  onVersionChange: (content: string, version?: number) => void;
}) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const { showError } = useSnackbar();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (versions.length) {
      setAnchorEl(event.currentTarget);
    }
  };

  const open = Boolean(anchorEl);

  const { theme } = useTheme();

  // 设置 CSS 变量用于主题
  const popoverStyle = {
    '--popover-bg': theme === 'dark' ? '#1a1a1a' : '#ffffff',
    '--popover-text': theme === 'dark' ? '#ffffff' : '#000000',
    '--popover-hover': theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
    '--popover-divider': theme === 'dark' ? '#333333' : '#e5e5e5',
    '--popover-border': theme === 'dark' ? '#333333' : '#e5e5e5',
  } as React.CSSProperties;

  useEffect(() => {
    (async () => {
      try {
        let id = message.messageId;
        try {
          const r = await researchFetch(`api/search/${id}/report-id`).then((r) => r?.json());
          id = await r?.data?.messageId;
        } catch (error) {}
        const resp = await researchFetch(
          `api/conversations/${message.chatId}/messages/${id || message.messageId}/versions`,
        );
        const res = await resp?.json();
        if (res.success) {
          setVersions(res.data.versions);
        } else {
          showError('get report version failed, please try again later!');
        }
      } catch (errer) {
        showError((errer as any).error || (errer as any).message || 'get report version failed');
      }
    })();
  }, [message.chatId, message.messageId]);

  return (
    <div className="flex items-center justify-between w-full pr-4">
      <span>Edit</span>
      <button onClick={handleClick} className={versions.length ? '' : 'cursor-not-allowed'}>
        <History size={16} stroke={versions.length > 0 ? 'white' : '#2a2a2a'} />
      </button>
      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={popoverStyle}
      >
        <div className="p-1 max-h-[400px] overflow-y-auto text-xs!">
          <MenuItem
            onClick={() => {
              const last = versions.at(-1);
              if (last) {
                onVersionChange(last.content, last.versionNumber);
              }
            }}
            className="flex-col! items-start! gap-0!"
          >
            <p>Restore last version</p>
          </MenuItem>
          {versions.map((v) => (
            <MenuItem
              key={v.versionNumber}
              onClick={() => onVersionChange(v.content, v.versionNumber)}
              className="flex-col! items-start! gap-0!"
            >
              <p>{v.title}</p>
              <p className="text-xs text-text-tertiary">
                {dayjs(v.createdAt).format('MMM DD, YYYY HH:mm')}
              </p>
            </MenuItem>
          ))}
        </div>
      </StyledPopover>
    </div>
  );
}
